import { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import Icon from './Icon';
import './MemberSidebar.css';
import logo from '../assets/logo.png';
import { DECISIONS } from '../data/decisionsData';
import { getUserDisplayName, hasBoardPagePermission } from '../authStorage';
import { useT } from '../i18n/translations';

function buildNavGroups(t, pendingCount) {
  return [
    {
      sectionKey: 'nav.home',
      items: [
        { to: '/member/dashboard',    labelKey: 'common.dashboard',    icon: 'dashboard', moduleId: 'member_dashboard'  },
      ],
    },
    {
      sectionKey: 'common.decisions',
      items: [
        { to: '/member/decisions',    labelKey: 'common.decisions',    icon: 'decisions', moduleId: 'member_decisions',  badge: pendingCount > 0 ? String(pendingCount) : null },
        { to: '/member/committees',   labelKey: 'nav.committees',      icon: 'board',     moduleId: 'member_committees'  },
        { to: '/member/meetings',     labelKey: 'common.myMeetings',   icon: 'calendar',  moduleId: 'member_meetings'    },
        { to: '/member/history',      labelKey: 'nav.myVotingHistory', icon: 'report',    moduleId: 'member_history'     },
      ],
    },
    {
      sectionKey: 'nav.management',
      items: [
        { to: '/member/users',        labelKey: 'common.users',        icon: 'user',      moduleId: 'member_users'       },
        { to: '/member/permissions',  labelKey: 'common.permissions',  icon: 'lock',      moduleId: 'member_permissions' },
      ],
    },
    {
      sectionKey: 'nav.account',
      items: [
        { to: '/member/settings',     labelKey: 'common.settings',     icon: 'settings'                                  },
      ],
    },
  ];
}

function MemberSidebar({ isOpen, onClose }) {
  const t = useT();
  const [collapsed, setCollapsed] = useState(false);
  const displayName = getUserDisplayName() || t('common.user');

  const pendingCount = useMemo(() => DECISIONS.filter((d) => !d.myVote && d.status === 'pending').length, []);
  const NAV_GROUPS   = useMemo(() => buildNavGroups(t, pendingCount), [t, pendingCount]);

  return (
    <aside className={`sidebar member-sidebar${collapsed ? ' sidebar--collapsed' : ''}${isOpen ? ' sidebar--mobile-open' : ''}`}>

      <div className="sidebar__brand">
        <img src={logo} alt={t('common.brand')} className="sidebar__brand-logo" />
        <div className="sidebar__brand-text">
          <div className="sidebar__brand-name">{t('common.brand')}</div>
          <div className="sidebar__brand-tagline">{t('common.brandTagline')}</div>
        </div>
        <button type="button" className="sidebar__mobile-close" onClick={onClose} aria-label={t('common.closeMenu')}>
          <Icon name="x-circle" size={18} color="rgba(255,255,255,0.7)" />
        </button>
      </div>

      <div className="sidebar__user member-sidebar__user">
        <div className="sidebar__user-avatar" style={{ background: '#02c3a6' }}>BM</div>
        <div className="sidebar__user-info">
          <div className="sidebar__user-name">{displayName}</div>
          <div className="sidebar__user-role member-role-badge">
            <span className="member-role-dot" />
            {t('nav.activeMember')}
          </div>
        </div>
        <span className="sidebar__user-dot" />
      </div>

      <nav className="sidebar__nav">
        {NAV_GROUPS.map((group) => (
          <div key={group.sectionKey}>
            <div className="sidebar__section-title">{t(group.sectionKey)}</div>
            {group.items.filter((item) => !item.moduleId || hasBoardPagePermission(item.moduleId)).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `sidebar__item${isActive ? ' sidebar__item--active' : ''}`}
                data-label={t(item.labelKey)}
              >
                <span className="sidebar__item-icon">
                  <Icon name={item.icon} size={18} color="rgba(255,255,255,0.85)" />
                </span>
                <span className="sidebar__item-label">{t(item.labelKey)}</span>
                {item.badge && <span className="sidebar__badge">{item.badge}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <button type="button" className="sidebar__toggle" onClick={() => setCollapsed(!collapsed)}>
        <span className="sidebar__toggle-icon">
          <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={15} color="rgba(255,255,255,0.5)" />
        </span>
        <span className="sidebar__toggle-label">{t('common.collapseMenu')}</span>
      </button>
    </aside>
  );
}

export default MemberSidebar;
